import {
  InspectRecommend,
  InspectRecommendCategory,
  InspectRecommendSubItem
} from "@/model/InspectRecommend";
import {
  RecMaintenanceCurrentDataApplyState,
  RecMaintenanceChangeShowApplyState,
  RecMaintenanceCategoryStatusApplyState,
  RecMaintenanceCloseApplyState,
  RecMaintenanceConfirmChangeApplyState,
  RecMaintenanceHideAllItemApplyState,
  RecMaintenanceInitialDataState,
  RecMaintenanceManualBuhinDeleteApplyState,
  RecMaintenanceManualItemAddSuccessState,
  RecMaintenancePageService as RecMaintenancePageServiceInterface,
  RecMaintenanceShowAllItemApplyState,
  RecMaintenanceWorkToggleApplyState,
  RecMaintenanceSaveSuccessApplyState,
  RecMaintenanceSaveQueueItemState
} from "@/model/RecMaintenancePageService";
import { INSPECT_TYPE } from "@/model/InspectType";
import { STATUS_SELECT_WORK_BUHIN } from "@/model/StatusSelectWorkBuhin";
import {
  buildRecMaintenanceAddedManualItem,
  buildRecMaintenanceChangeShowState,
  buildRecMaintenanceCategoryStatusInput,
  buildRecMaintenanceCloseState,
  buildRecMaintenanceConfirmChangeState,
  buildRecMaintenanceCurrentDataState,
  buildRecMaintenanceCurrentDataSaveItems,
  buildRecMaintenanceCurrentDataSavePayload,
  buildRecMaintenanceSavePayload,
  buildRecMaintenanceSaveItemGroups,
  buildRecMaintenanceSaveQueueItemBase,
  buildRecMaintenanceSaveQueueItemWithCategory,
  buildRecMaintenanceSaveSuccessState,
  buildRecMaintenanceSavingCompleteKeys,
  buildRecMaintenanceHideAllItemState,
  buildRecMaintenanceManualAddItem,
  buildRecMaintenanceManualBuhinDeleteState,
  buildRecMaintenanceManualInputResetState,
  buildRecMaintenanceOsusumeBuhinListWithoutSelection,
  buildRecMaintenanceShowAllItemState,
  buildRecMaintenanceRouteInfo,
  buildRecMaintenanceWorkToggleState,
  findRecMaintenanceSeibiIndex,
  getRecMaintenanceShowButtonClass,
  getRecMaintenanceTooltipMessage,
  getRecMaintenanceWorkButtonClass,
  getRecMaintenanceWorkButtonLabel,
  isRecMaintenanceShowAll,
  isRecMaintenanceWorkDisabled,
  resolveRecMaintenanceSocketFlag,
  resolveRecMaintenanceCategoryStatus,
  resolveRecMaintenanceConfirmItem,
  resolveRecMaintenanceCategoryRestorePos,
  resolveRecMaintenanceCategoryId,
  resolveRecMaintenanceLastSelectedPos,
  shouldRecMaintenanceSaveBeforeDestroy,
  upsertRecMaintenanceSaveQueue,
  buildRecMaintenanceSaveTargetItems
} from "@/components/work-buhin/WorkBuhinRecMaintenance";
import { InspectButtonStatus } from "@/model/InspectButtonStatus";
import requestService from "@/service/requestService";

export class RecMaintenancePageService implements RecMaintenancePageServiceInterface {
  private reqService: requestService;

  constructor() {
    this.reqService = new requestService();
  }

  getInspectRecommend(uketsukeExtendsId: number): Promise<InspectRecommend> {
    return this.reqService.getInspectRecommend(uketsukeExtendsId) as Promise<InspectRecommend>;
  }

  getQmMitsumoriPreview(uketsukeExtendsId: number): Promise<any> {
    return this.reqService.getQmMitsumoriPreview(uketsukeExtendsId);
  }

  saveInspectRecommend(data: any): Promise<any> {
    return this.reqService.saveInspectRecommend(data);
  }

  saveInspectRecommendQm(data: any): Promise<any> {
    return this.reqService.saveInspectRecommendQm(data);
  }

  saveInspectRecommendManual(data: any): Promise<any> {
    return this.reqService.saveInspectRecommendManual(data);
  }

  deleteManualBuhin(id: number): Promise<any> {
    return this.reqService.deleteManualBuhin(id);
  }

  saveCancel(data: any): Promise<any> {
    return this.reqService.saveCancel(data);
  }

  /*
   * WHY: recMaintenance の初期表示は API 応答、カテゴリ status、行番号最大値を同時に整えるため、
   * page に recommend 配列の初期化手順を持たせない。
   */
  buildInitialDataState(
    res: InspectRecommend | null | undefined,
    changeStatus: (item: InspectRecommendSubItem, checkCate?: boolean) => void,
    setCateStatus: (
      sumStatus: number,
      nItem: number,
      cate?: InspectRecommendCategory
    ) => void,
    maxRowNum: number
  ): RecMaintenanceInitialDataState {
    if (!res || !res.recommend) {
      return {
        hasRecommend: false,
        data: {},
        categories: [],
        maxRowNum
      };
    }

    const categories = res.recommend.map((cate: InspectRecommendCategory) => {
      let sumStatus = 0;
      cate.subItems = cate.subItems.map((obj: InspectRecommendSubItem) => {
        changeStatus(obj, false);
        if (obj.status) {
          sumStatus += obj.status;
        }
        if (obj.rowNum > maxRowNum) {
          maxRowNum = obj.rowNum;
        }
        return {
          ...obj,
          showWork: false
        };
      });
      setCateStatus(sumStatus, cate.subItems.length, cate);
      return cate;
    });

    return {
      hasRecommend: true,
      data: { recommend: categories },
      categories,
      maxRowNum
    };
  }

  /*
   * WHY: 表示中カテゴリ全体の保存 payload は削除 status や手入力 type を含むため、
   * page から API 送信用構造の詳細を隠す。
   */
  buildCurrentDataSavePayload(params: {
    currentData: InspectRecommendSubItem[];
    data: InspectRecommend;
    activeCategory: number;
    uketsukeExtendsId: number;
  }): any | null {
    if (!params.data.recommend) {
      return null;
    }
    const category = params.data.recommend[params.activeCategory];
    const id = category.sknRecommendInspectCategoryId;
    const items = buildRecMaintenanceCurrentDataSaveItems(
      params.currentData,
      id,
      STATUS_SELECT_WORK_BUHIN.delete
    );
    return buildRecMaintenanceCurrentDataSavePayload({
      items,
      category,
      uketsukeExtendsId: params.uketsukeExtendsId,
      doneStatus: STATUS_SELECT_WORK_BUHIN.Done,
      deleteStatus: STATUS_SELECT_WORK_BUHIN.delete,
      recommendManualType: INSPECT_TYPE.RECOMMEND_MANUAL
    });
  }

  /*
   * WHY: 単品保存と待機 queue 保存で対象 item の取り方が異なるため、
   * 保存 payload の組み立てを service に集約する。
   */
  buildSavePayload(params: {
    item: any;
    tempListSave: InspectRecommendSubItem[];
    data: InspectRecommend;
    activeCategory: number;
    uketsukeExtendsId: number;
    isSocket: number;
  }): any {
    const tempItems = params.item || params.tempListSave;
    const items = buildRecMaintenanceSaveTargetItems(params.item, tempItems);
    const saveItemGroups = buildRecMaintenanceSaveItemGroups({
      items,
      data: params.data,
      activeCategory: params.activeCategory,
      uketsukeExtendsId: params.uketsukeExtendsId,
      doneStatus: STATUS_SELECT_WORK_BUHIN.Done,
      deleteStatus: STATUS_SELECT_WORK_BUHIN.delete,
      recommendManualType: INSPECT_TYPE.RECOMMEND_MANUAL
    });
    return buildRecMaintenanceSavePayload({
      isSocket: params.isSocket,
      subItems: saveItemGroups.subItems,
      manualItems: saveItemGroups.manualItems
    });
  }

  /*
   * WHY: 保存 queue に積む item はカテゴリ ID を補完する必要があるため、
   * page には queue へ入れる state だけを返す。
   */
  buildSaveQueueItemWithCategory(params: {
    inspectItem: InspectRecommendSubItem;
    data: InspectRecommend;
    activeCategory: number;
  }): RecMaintenanceSaveQueueItemState {
    const saveItemBase = buildRecMaintenanceSaveQueueItemBase(params.inspectItem);
    const item = buildRecMaintenanceSaveQueueItemWithCategory({
      item: saveItemBase.item,
      data: params.data,
      activeCategory: params.activeCategory
    });
    return {
      status: saveItemBase.status,
      item
    };
  }

  /*
   * WHY: activeCategory から表示中データと金額編集可否を取り出す処理を
   * page のカテゴリ制御から分離する。
   */
  buildCurrentDataApplyState(params: {
    data: InspectRecommend;
    activeCategory: number;
  }): RecMaintenanceCurrentDataApplyState {
    const currentDataState = buildRecMaintenanceCurrentDataState(
      params.data,
      params.activeCategory
    );
    return {
      hasCategory: currentDataState.hasCategory,
      kingakuFlg: currentDataState.kingakuFlg,
      currentData: currentDataState.currentData as InspectRecommendSubItem[]
    };
  }

  /*
   * WHY: 保存成功後に消す saving key と未保存 queue の再構築条件を service に閉じ込める。
   */
  buildSaveSuccessApplyState(params: {
    savePayload: any;
    tempWaitSave: any[];
  }): RecMaintenanceSaveSuccessApplyState {
    const saveSuccessState = buildRecMaintenanceSaveSuccessState(
      params.tempWaitSave
    );
    return {
      savedKeys: buildRecMaintenanceSavingCompleteKeys(params.savePayload),
      hasPendingSave: saveSuccessState.hasPendingSave,
      pendingItems: saveSuccessState.pendingItems,
      tempWaitSave: saveSuccessState.tempWaitSave,
      tempListSave: saveSuccessState.tempListSave,
      arrStatus: saveSuccessState.arrStatus
    };
  }

  /*
   * WHY: 手入力おすすめ追加後の item 生成と入力欄 reset は同じ業務操作なので、
   * page に生成 field の詳細を持たせない。
   */
  buildManualItemAddSuccessState(params: {
    uketsukeExtendsId: number;
    seibiId: any;
    seibiNm: string;
    kouchinZn: any;
    data: InspectRecommend;
    activeCategory: number;
    rowNum: number;
  }): RecMaintenanceManualItemAddSuccessState {
    const item = buildRecMaintenanceAddedManualItem({
      uketsukeExtendsId: params.uketsukeExtendsId,
      seibiId: params.seibiId,
      seibiNm: params.seibiNm,
      kouchinZn: params.kouchinZn,
      doneStatus: STATUS_SELECT_WORK_BUHIN.Done,
      completedStatus: InspectButtonStatus.COMPLETED,
      recommendManualType: INSPECT_TYPE.RECOMMEND_MANUAL,
      categoryId: resolveRecMaintenanceCategoryId(
        params.data,
        params.activeCategory,
        0
      ),
      rowNum: params.rowNum
    });
    const resetState = buildRecMaintenanceManualInputResetState();
    return {
      item,
      seibiNm: resetState.seibiNm,
      kouchinZn: resetState.kouchinZn,
      kingakuFlg: resetState.kingakuFlg
    };
  }

  /*
   * WHY: 一括「提案する」は status 遷移の業務値を伴うため、画面から定数比較を隠す。
   */
  buildShowAllItemApplyState(
    item: InspectRecommendSubItem
  ): RecMaintenanceShowAllItemApplyState {
    return {
      state: buildRecMaintenanceShowAllItemState(
        item,
        STATUS_SELECT_WORK_BUHIN.NoNeed,
        STATUS_SELECT_WORK_BUHIN.Done,
        STATUS_SELECT_WORK_BUHIN.Initial
      )
    };
  }

  /*
   * WHY: 一括「しない」の更新可否と item state を service で作り、
   * page は適用と保存判断だけを行う。
   */
  buildHideAllItemApplyState(
    item: InspectRecommendSubItem
  ): RecMaintenanceHideAllItemApplyState {
    return buildRecMaintenanceHideAllItemState(item);
  }

  /*
   * WHY: おすすめ表示切替時の activeInspect と item 更新を service に集約し、
   * page から osusumeIsShow の状態遷移を隠す。
   */
  buildChangeShowApplyState(
    item: InspectRecommendSubItem
  ): RecMaintenanceChangeShowApplyState {
    return buildRecMaintenanceChangeShowState(item);
  }

  /*
   * WHY: 表示切替の確認後に部品を消すかどうかは status と完了状態に依存するため、
   * page には適用結果だけを返す。
   */
  buildConfirmChangeApplyState(
    item: InspectRecommendSubItem
  ): RecMaintenanceConfirmChangeApplyState {
    return buildRecMaintenanceConfirmChangeState(
      item,
      InspectButtonStatus.COMPLETED,
      STATUS_SELECT_WORK_BUHIN.Initial
    );
  }

  /*
   * WHY: WorkAndBuhin 確定対象は activeInspect を基準にする仕様なので、
   * page 側で親子 item を探し直さない。
   */
  resolveConfirmItem(item: InspectRecommendSubItem): InspectRecommendSubItem {
    return resolveRecMaintenanceConfirmItem(item);
  }

  /*
   * WHY: カテゴリ遷移時に route store へ保存する情報の形を service に固定する。
   */
  buildRouteInfo(recommend: any, pos: number): any {
    return buildRecMaintenanceRouteInfo(recommend, pos);
  }

  /*
   * WHY: 最後に選択したカテゴリ復元の有無と位置計算を page の navigation から分離する。
   */
  resolveLastSelectedPos(params: {
    hasLastSelectedCategoryId: boolean;
    lastCategoryInfo: any;
  }): number {
    return resolveRecMaintenanceLastSelectedPos(
      params.hasLastSelectedCategoryId,
      params.lastCategoryInfo
    );
  }

  /*
   * WHY: 復元対象カテゴリが一覧外になった場合の fallback を service に閉じ込める。
   */
  resolveCategoryRestorePos(params: {
    hasLastSelectedCategoryId: boolean;
    lastCategoryInfo: any;
    length: number;
  }): number {
    return resolveRecMaintenanceCategoryRestorePos(
      params.hasLastSelectedCategoryId,
      params.lastCategoryInfo,
      params.length
    );
  }

  /*
   * WHY: socket 通知要否は保存待ち status と activeInspect の状態で決まるため、
   * page から条件式を隠す。
   */
  resolveSocketFlag(params: {
    arrStatus: number[];
    activeInspect: any;
  }): number {
    return resolveRecMaintenanceSocketFlag(
      params.arrStatus,
      InspectButtonStatus.COMPLETED,
      params.activeInspect
    );
  }

  /*
   * WHY: 保存済み item と表示 item の同一性判定を service に置き、
   * page が ID 比較の詳細を知らないようにする。
   */
  findSeibiIndex(list: any, seibiSave: any): number {
    return findRecMaintenanceSeibiIndex(list, seibiSave);
  }

  /*
   * WHY: 保存 queue の重複更新条件を service に集約し、page は queue の代入だけにする。
   */
  upsertSaveQueue(list: any[], item: any): any[] {
    return upsertRecMaintenanceSaveQueue(list, item);
  }

  /*
   * WHY: 手入力おすすめの追加 item にはカテゴリ ID と業務 type の補完が必要なため、
   * page には入力値だけを渡させる。
   */
  buildManualAddItem(params: {
    uketsukeExtendsId: number;
    seibiNm: string;
    kouchinZn: any;
    data: InspectRecommend;
    activeCategory: number;
  }): any {
    return buildRecMaintenanceManualAddItem({
      uketsukeExtendsId: params.uketsukeExtendsId,
      seibiNm: params.seibiNm,
      kouchinZn: params.kouchinZn,
      doneStatus: STATUS_SELECT_WORK_BUHIN.Done,
      recommendManualType: INSPECT_TYPE.RECOMMEND_MANUAL,
      categoryId: resolveRecMaintenanceCategoryId(
        params.data,
        params.activeCategory,
        0
      )
    });
  }

  /*
   * WHY: WorkAndBuhin 開閉時の showWork と activeInspect 更新を service state として返し、
   * page から item mutation の判断を減らす。
   */
  buildWorkToggleApplyState(item: any): RecMaintenanceWorkToggleApplyState {
    return buildRecMaintenanceWorkToggleState(item);
  }

  /*
   * WHY: 手入力部品削除後に activeInspect を空にするかどうかを service で判断する。
   */
  buildManualBuhinDeleteApplyState(
    item: any
  ): RecMaintenanceManualBuhinDeleteApplyState {
    return buildRecMaintenanceManualBuhinDeleteState(item);
  }

  /*
   * WHY: WorkAndBuhin close event の保存要否と socket flag を service に集約する。
   */
  buildCloseApplyState(event: any, activeInspect: any): RecMaintenanceCloseApplyState {
    return buildRecMaintenanceCloseState(event, activeInspect);
  }

  /*
   * WHY: カテゴリ status は表示中 item 全体の集計から決まるため、page から集計式を隠す。
   */
  buildCategoryStatusApplyState(
    currentData: InspectRecommendSubItem[]
  ): RecMaintenanceCategoryStatusApplyState {
    return buildRecMaintenanceCategoryStatusInput(currentData);
  }

  /*
   * WHY: カテゴリ status の NONE/COMPLETED/INCOMPLETE 判定に業務定数が必要なため、
   * page では sum と件数だけを扱う。
   */
  resolveCategoryStatus(params: {
    sumStatus: number;
    nItem: number;
  }): number {
    return resolveRecMaintenanceCategoryStatus(
      params.sumStatus,
      params.nItem,
      InspectButtonStatus.NONE,
      InspectButtonStatus.COMPLETED,
      InspectButtonStatus.INCOMPLETE
    );
  }

  /*
   * WHY: 保存 payload から UI 選択状態を除去する処理を service に置き、
   * page に osusumeBuhinList の内部 field を意識させない。
   */
  buildOsusumeBuhinListForSave(osusumeBuhinList: any[]): any[] {
    return buildRecMaintenanceOsusumeBuhinListWithoutSelection(osusumeBuhinList);
  }

  /*
   * WHY: quick 系 route では beforeDestroy 保存を抑制する仕様があるため、
   * routeName 判定を page から service へ移す。
   */
  shouldSaveBeforeDestroy(params: {
    timerSave: any;
    tempListSave: any[];
    routeName: any;
  }): boolean {
    const quickRoutes = ["quickitemselection", "quickmitsumori", "quickinfo"];
    return shouldRecMaintenanceSaveBeforeDestroy({
      timerSave: params.timerSave,
      tempListSave: params.tempListSave,
      routeName: params.routeName,
      quickRoutes
    });
  }

  /*
   * WHY: 作業/部品ボタン文言は buhinSelected に依存するため、template に業務値を出さない。
   */
  getWorkButtonLabel(item: any): string {
    return getRecMaintenanceWorkButtonLabel(item);
  }

  /*
   * WHY: 作業/部品ボタン class は未完了判定と選択状態を組み合わせるため、
   * template の条件を service に寄せる。
   */
  getWorkButtonClass(item: any, isIncomplete: boolean): string {
    return getRecMaintenanceWorkButtonClass(item, isIncomplete);
  }

  /*
   * WHY: 提案ボタン class を osusumeIsShow の業務値から切り離して template を薄くする。
   */
  getShowButtonClass(item: any): string {
    return getRecMaintenanceShowButtonClass(item);
  }

  /*
   * WHY: 一括表示状態の判定を service に置き、page では currentData と対象 show 値だけを渡す。
   */
  isShowAll(items: any[], show: number): boolean {
    return isRecMaintenanceShowAll(items, show);
  }

  /*
   * WHY: 作業/部品ボタンの disabled 条件を service に集約し、template から状態値の直接判定を隠す。
   */
  isWorkDisabled(item: any): boolean {
    return isRecMaintenanceWorkDisabled(item);
  }

  /*
   * WHY: 重複時 tooltip の文言選択を service に置き、template には問い合わせだけを残す。
   */
  getTooltipMessage(isDuplicate: boolean): string {
    return getRecMaintenanceTooltipMessage(isDuplicate);
  }
}
